/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { ReferenceStoreDto } from '@ink-ai/insight-service-sdk';
import { isOffice } from '@ink-ai/portal/common/utils';
import { ReferenceStoreTable } from './ReferenceStoreListTable';
import { referenceStoreActions } from '@ink-ai/portal/reducers/reference-store';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

interface ReferenceStoreDialogProps {
  open: boolean;
  onClose: () => void;
  onApply: (selectedReferenceStores: string[], selectedFolders: string[]) => void;
  initialSelectedReferenceStores?: string[];
  initialSelectedFolders?: string[];
  // Enable cascading selection behavior on folder selection
  cascadeSelection?: boolean;
  // 控制是否在切换文件夹/返回根目录时重置勾选（列表页可能需要，翻译页不需要）
  resetSelectionOnFolderNav?: boolean;
}

export const ReferenceStoreDialog: React.FC<ReferenceStoreDialogProps> = ({
  open,
  onClose,
  onApply,
  initialSelectedReferenceStores,
  initialSelectedFolders,
  cascadeSelection = false,
  resetSelectionOnFolderNav = true,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<any>();
  const [selectedReferenceStores, setSelectedReferenceStores] = useState<
    string[]
  >([]);
  const [selectedReferenceStoreFolders, setSelectedReferenceStoreFolders] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [totalItemCount, setTotalItemCount] = useState(0);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [folderStack, setFolderStack] = useState<{ id: string | null; name: string }[]>([{ id: null, name: t('referenceStore.navigation.referenceStore') }]);

  const handleApply = () => {
    onApply(selectedReferenceStores, selectedReferenceStoreFolders);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNavigate = (referenceStore: ReferenceStoreDto) => {
    dispatch(referenceStoreActions.setSelectedReferenceStore(referenceStore));
    window.open(`/reference-store/${referenceStore.uuid}`, '_blank');
  };

  // Load explorer items (root or specific folder)
  const loadExplorer = async () => {
    try {
      const params: Record<string, any> = {
        page: page + 1,
        pageSize: rowsPerPage,
      };
      if (searchTerm && searchTerm.trim().length > 0)
        params.name = searchTerm.trim();
      if (currentFolderId) params.parentId = currentFolderId;
      params.status = 'Ready';
      const data: any = await makeRequest({
        method: 'GET',
        url: '/api/v1/referenceStore/referenceExplorer',
        params,
      });
      const rawItems = data.collections || data.data?.collections || data.items || data.data?.items || [];
      const total = data.totalCount ?? data.data?.totalCount ?? rawItems.length;
      setTotalItemCount(total);
      const toMillis = (v: any) => (typeof v === 'number' ? v : (v ? new Date(v).getTime() : undefined));
      const itemsMapped = (rawItems as any[]).map((it: any) => {
        const isFolder = String(it.itemType) === 'folder';
        const isFile = String(it.itemType) === 'file' || (!it.itemType && (it.status || it.itemCount !== undefined));
        const childrenRaw: any[] = Array.isArray(it.collections) ? it.collections : [];
        const children = childrenRaw
          .filter((c) => String(c.itemType) !== 'folder')
          .map((c) => ({
            uuid: c.uuid,
            type: 'reference',
            status: c.status,
            parentId: it.uuid,
          }));
        return {
          uuid: it.uuid,
          name: it.name,
          type: isFolder ? 'folder' : 'reference',
          desc: isFile ? it.desc : undefined,
          status: isFile ? it.status : undefined,
          createdAt: toMillis(it.createdAt),
          updatedAt: toMillis(it.updatedAt),
          itemCount: isFile ? (it.itemsCount ?? it.itemCount) : undefined,
          parentId: it.parentId ?? currentFolderId ?? null,
          children: isFolder ? children : undefined,
        };
      });
      setItems(itemsMapped);
    } catch (e) {
      console.error('Failed to load reference explorer in dialog:', e);
    }
  };

  // 仅负责加载列表（随页码/搜索/文件夹变化）
  useEffect(() => {
    if (!open) return;
    loadExplorer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage, searchTerm, currentFolderId]);

  // 同步初始选中项到内部状态：只在打开弹窗或初始值变化时进行
  useEffect(() => {
    if (!open) return;
    setSelectedReferenceStores(initialSelectedReferenceStores ?? []);
    setSelectedReferenceStoreFolders(initialSelectedFolders ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialSelectedReferenceStores, initialSelectedFolders]);

  // 可选：在切换文件夹时重置（用于列表页需求）
  useEffect(() => {
    if (!open || !resetSelectionOnFolderNav) return;
    setSelectedReferenceStores(initialSelectedReferenceStores ?? []);
    setSelectedReferenceStoreFolders(initialSelectedFolders ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, resetSelectionOnFolderNav, currentFolderId]);

  const currentFolderName = useMemo(
    () => folderStack[folderStack.length - 1]?.name,
    [folderStack],
  );

  const handleEnterFolder = (folderId: string, folderName?: string) => {
    setCurrentFolderId(folderId);
    setFolderStack((prev) => [
      ...prev,
      { id: folderId, name: folderName || t('referenceStore.title') },
    ]);
    setPage(0);
  };

  const handleBack = () => {
    if (folderStack.length <= 1) return;
    const next = [...folderStack];
    next.pop();
    setFolderStack(next);
    setCurrentFolderId(next[next.length - 1]?.id || null);
    setPage(0);
  };

  const isInOffice = isOffice();

  return (
    <Dialog
      fullScreen={isInOffice}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {folderStack.length > 1 ? (
          <IconButton aria-label="back" onClick={handleBack} size="small">
            <ArrowBackOutlinedIcon />
          </IconButton>
        ) : null}
        {currentFolderId ? currentFolderName : t('referenceStore.dialog.chooseStores')}
      </DialogTitle>
      <DialogContent>
        <TextField
          label={t('common.search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{ mb: 2 }}
          InputLabelProps={{
            shrink: true,
          }}
          margin="dense"
        />
        <ReferenceStoreTable
          items={items}
          selectedReferenceStores={selectedReferenceStores}
          selectedFolders={selectedReferenceStoreFolders}
          onSelectedReferenceStoresChange={setSelectedReferenceStores}
          onSelectedFoldersChange={setSelectedReferenceStoreFolders}
          searchTerm={searchTerm}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          onNavigate={handleNavigate}
          onEnterFolder={handleEnterFolder}
          allowNonReadySelection
          enableSelection
          showActions={false}
          cascadeSelection={cascadeSelection}
          totalCount={totalItemCount}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={handleApply} color="primary" variant="contained">
          {t('common.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
